import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Typography, withStyles } from '@material-ui/core';
import SectionTitle from '../../shared/sectionTitle';
import JobTitle from '../../shared/careerPerformance/jobTitle';
import CareerPerformance from '../../shared/careerPerformance';
import CareerProgress from '../../shared/careerProgress';
import CustomFormDrawer from '../../shared/customFormDrawer';
import { PARAMS } from '../../../constants/pages';
import { API_ATTRIBUTES } from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import {
  SURVEY_TYPES,
  CREATE_SURVEY_FIELD_NAMES,
} from '../../../constants/survey';
import http from '../../../utility/http';
import {
  formatDate,
  getLast12Months,
} from '../../shared/customDateRange/config';
import { getStartEndOfDayUtc } from '../../../utility/dateUtils';
import {
  isArrayEmpty,
  isUserDeactivated,
  isObjectEmpty,
  getItemById,
} from '../../../utility/helpers';
import {
  hasScopedAuthPermissionCheck,
  hasAuthPermissionsCheck,
} from '../../../utility/permissions';
import { hasQuantitativeAttributeScores } from '../../../utility/attribute';
import {
  reformatUserForSurvey,
  reformatAttributeForSurvey,
  showSuccessMessage,
  prepareTracksWithLevels,
  prepareFrameworksWithTracks,
  prepareAllTracksLevels,
} from '../../../utility/uiUtils';
import { prepareTrackLevels, prepareCareerInfoFields } from './config';

const styles = ({ palette: { primary }, spacing }) => ({
  headerBlock: {
    ...sticky(primary.white, 170),
  },
  title: {
    marginBottom: spacing(4),
  },
  description: {
    paddingBottom: spacing(7.5),
    fontSize: 16,
    lineHeight: '20.8px',
  },
  progress: {
    marginTop: spacing(9),
  },
});

const DATE_FORMAT = 'yyyy-LL-dd';

class PeopleCareerPage extends PureComponent {
  state = {
    isLoading: true,
    isDrawerOpened: false,
    currentLevelAttributes: [],
    period: getLast12Months(),
  };

  componentDidMount() {
    const {
      accessControl,
      auth,
      user,
      getJobTitles,
      getLevelsAndTracks,
      getPersonJobTitle,
      getMeasuredAttributes,
    } = this.props;

    if (user.id !== -1) {
      const canEdit = hasScopedAuthPermissionCheck(
        PERMISSION_CODES.PROFILES_EDIT,
        accessControl,
        auth.id,
        user.id
      );

      Promise.all([
        ...(user?.position?.id ? [getPersonJobTitle(user.position?.id)] : []),
        ...(canEdit ? [getJobTitles()] : []),
        getLevelsAndTracks(),
      ]).then(() => {
        const { tracks } = this.props;
        const levels = prepareAllTracksLevels(tracks);
        const currentLevel = getItemById(levels, user.level);
        const currentLevelAttributes = currentLevel?.attributes;
        const period = this.handleParams();

        getMeasuredAttributes(user.id, currentLevelAttributes, { period }).then(
          () =>
            this.setState({
              isLoading: false,
              currentLevelAttributes,
            })
        );
      });
    }
  }

  componentWillUnmount() {
    const {
      clearJobTitles,
      clearLevelsAndTracks,
      clearPersonCareer,
      clearLevelingFrameworks,
    } = this.props;

    clearJobTitles();
    clearLevelsAndTracks();
    clearPersonCareer();
    clearLevelingFrameworks();
  }

  handleParams = (newPeriod = {}) => {
    const { START, END } = PARAMS;
    const period = isObjectEmpty(newPeriod) ? this.state.period : newPeriod;

    return {
      [START]: getStartEndOfDayUtc(period[START]),
      [END]: getStartEndOfDayUtc(period[END], true),
    };
  };

  handleSendSurvey = (attributeId, isSelf = false) => {
    const { navigate, user, auth } = this.props;
    const { SELF, PEER } = SURVEY_TYPES;
    const { SURVEY_TYPE, CREATED_FOR, REVIEWERS, ATTRIBUTES } =
      CREATE_SURVEY_FIELD_NAMES;

    return http.get(`${API_ATTRIBUTES}${attributeId}/`).then(({ data }) => {
      navigate('/surveys', {
        state: {
          surveyData: {
            [SURVEY_TYPE]: isSelf ? SELF : PEER,
            [CREATED_FOR]: Array.of(reformatUserForSurvey(user)) || [],
            [REVIEWERS]:
              auth.id === user.id ? [] : Array.of(reformatUserForSurvey(auth)),
            [ATTRIBUTES]: Array.of(reformatAttributeForSurvey(data)) || [],
          },
        },
      });
    });
  };

  handleAddJobTitle = () => {
    const { navigate } = this.props;

    navigate('/job-titles');
  };

  handleAddLevel = () => {
    const { navigate } = this.props;

    navigate('/levels-and-tracks');
  };

  onRedirectToCompareReport = attributes => {
    const { navigate, user } = this.props;
    const { START, END } = PARAMS;
    const period = this.handleParams();
    const isExtrinsicCompare = hasQuantitativeAttributeScores(attributes);

    navigate('/reports/compare', {
      state: {
        user,
        ...(isExtrinsicCompare ? {} : { surveyType: [SURVEY_TYPES.SELF] }),
        period: {
          start: formatDate(period[START], DATE_FORMAT),
          end: formatDate(period[END], DATE_FORMAT),
        },
      },
    });
  };

  handleOpenCareerInfoForm = () => this.setState({ isDrawerOpened: true });

  handleCloseCareerInfoForm = () =>
    this.setState({
      isDrawerOpened: false,
    });

  handleSaveCareerInfo = values => {
    const {
      translations,
      user,
      updateEmploymentInfo,
      getPersonJobTitle,
      getMeasuredAttributes,
      tracks,
    } = this.props;
    const period = this.handleParams();
    const data = {
      position: values.jobTitle,
      track: values.track,
      level: values.level,
    };
    const levels = prepareAllTracksLevels(tracks);
    const currentLevel = getItemById(levels, values.level);
    const currentLevelAttributes = currentLevel?.attributes;

    return updateEmploymentInfo(user.id, data).then(async () => {
      if (values.jobTitle) {
        await getPersonJobTitle(values.jobTitle);
      }
      if (values.level) {
        await getMeasuredAttributes(user.id, currentLevelAttributes, {
          period,
        });
      }
      showSuccessMessage(translations.careerInfoSuccess);
    }, this.handleCloseCareerInfoForm);
  };

  handlePeriodChange = newPeriod => {
    const { getMeasuredAttributes, user } = this.props;
    const { currentLevelAttributes } = this.state;
    this.setState({ period: newPeriod });
    const period = this.handleParams(newPeriod);

    getMeasuredAttributes(user.id, currentLevelAttributes, { period }).then(
      () => this.setState({ currentLevelAttributes })
    );
  };

  render() {
    const {
      classes,
      translations,
      jobTitle,
      measuredAttributes,
      jobTitles,
      auth,
      accessControl,
      user,
      frameworks,
      tracks,
    } = this.props;
    const { isLoading, isDrawerOpened, period } = this.state;
    const canEdit = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PROFILES_EDIT,
      accessControl,
      auth.id,
      user.id
    );
    const [
      canManageJobTitle,
      canManageLevelsAndTracks,
      canManageAllSurveys,
      canManageSurveysCreated,
    ] = hasAuthPermissionsCheck(
      [
        PERMISSION_CODES.JOB_TITLES_MANAGE,
        PERMISSION_CODES.LEVELING_EDIT,
        PERMISSION_CODES.SURVEYS_MANAGE_ALL,
        PERMISSION_CODES.SURVEYS_MANAGE_CREATED,
      ],
      accessControl
    );
    const canViewCompareReport = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.REPORTS_COMPARE_VIEW,
      accessControl,
      auth.id,
      user.id
    );
    const tracksWithLevels = prepareTracksWithLevels(tracks);
    const frameworksWithTracks = prepareFrameworksWithTracks(frameworks);
    const jobTitlesExist = !isArrayEmpty(jobTitles);
    const tracksWithLevelsExist = !isArrayEmpty(tracksWithLevels);
    const currentUserTrack = getItemById(tracksWithLevels, user.track);
    const currentUserFramework = getItemById(
      frameworksWithTracks,
      currentUserTrack?.framework
    );
    const levelBreadcrumbs = currentUserTrack
      ? `${currentUserFramework.name} / ${currentUserTrack.name}`
      : '';
    const hasAddJobTitleButton = canManageJobTitle && !jobTitlesExist;
    const hasAssignJobTitleButton = canEdit && jobTitlesExist;
    const hasAddLevelButton =
      canManageLevelsAndTracks && !tracksWithLevelsExist;
    const hasAssignLevelButton = canEdit && tracksWithLevelsExist;
    const hasSendSurveyButton = canManageAllSurveys || canManageSurveysCreated;

    return (
      <div>
        {!isLoading && (
          <>
            <div className={classes.headerBlock}>
              <SectionTitle
                className={classes.title}
                title={translations.careerPerformance.title}
                variant="h4"
              />
              <Typography className={classes.description} variant="body2">
                {translations.description}
              </Typography>
            </div>
            <JobTitle
              className={classes.divider}
              translations={translations.careerPerformance}
              jobTitle={jobTitle}
              hasAssignJobTitleButton={hasAssignJobTitleButton}
              hasAddJobTitleButton={hasAddJobTitleButton}
              isAssignDisabled={isUserDeactivated(user)}
              onAddJobTitle={this.handleAddJobTitle}
              onAssignJobTitle={this.handleOpenCareerInfoForm}
            />
            <CareerProgress
              className={classes.progress}
              translations={translations.careerProgress}
              currentLevelId={user.level}
              levelBreadcrumbs={levelBreadcrumbs}
              levels={prepareTrackLevels(user.level, currentUserTrack)}
              hasAddLevelButton={hasAddLevelButton}
              hasAssignLevelButton={hasAssignLevelButton}
              isUserSuspended={isUserDeactivated(user)}
              onAssignLevel={this.handleOpenCareerInfoForm}
              onAddLevel={this.handleAddLevel}
              period={period}
              handlePeriodChange={newPeriod =>
                this.handlePeriodChange(newPeriod)
              }
              {...measuredAttributes}
            />
            <CareerPerformance
              translations={translations.careerPerformance}
              userLevel={user.level}
              hasCompareReportButton={canViewCompareReport}
              hasSendSurveyButton={hasSendSurveyButton}
              isUserSuspended={isUserDeactivated(user)}
              onRedirectToCompareReport={this.onRedirectToCompareReport}
              onSendSurvey={this.handleSendSurvey}
              {...measuredAttributes}
            />
          </>
        )}
        {canEdit && (
          <CustomFormDrawer
            isOpened={isDrawerOpened}
            translations={translations.addUserCareerForm}
            fields={prepareCareerInfoFields(!user.position, !user.level)}
            jobTitles={jobTitles}
            frameworks={frameworksWithTracks}
            tracks={tracksWithLevels}
            onClose={this.handleCloseCareerInfoForm}
            onSave={this.handleSaveCareerInfo}
          />
        )}
      </div>
    );
  }
}

PeopleCareerPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.object.isRequired,
  user: PropTypes.shape({}).isRequired,
  jobTitle: PropTypes.shape({}).isRequired,
  measuredAttributes: PropTypes.shape({
    core: PropTypes.arrayOf(PropTypes.object),
    niche: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  jobTitles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getJobTitles: PropTypes.func.isRequired,
  getLevelsAndTracks: PropTypes.func.isRequired,
  getPersonJobTitle: PropTypes.func.isRequired,
  getMeasuredAttributes: PropTypes.func.isRequired,
  updateEmploymentInfo: PropTypes.func.isRequired,
  clearJobTitles: PropTypes.func.isRequired,
  clearLevelsAndTracks: PropTypes.func.isRequired,
  clearLevelingFrameworks: PropTypes.func.isRequired,
  clearPersonCareer: PropTypes.func.isRequired,
};

export default withStyles(styles)(PeopleCareerPage);
